import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import classNames from 'classnames';
import MeasureSelectionDropdownItem from '../../../common/popups/MeasureSelectionDropdownItem';
import { GRADE_LABEL, PM_GRADE_AVAILABILITY } from '../../../constants/enrollment';
import {
    getSortedGrades,
    getSortedForms,
    getBurstSequence,
    formatBurstForm,
} from '../../../common/utils/prepareFormsData';
import FormSelectionPopup from '../../../common/popups/FormSelectionPopup';
import {
    constructStartPMAssessmentLink,
    linkOnClickHandler,
} from '../../../common/navigation/navigation';
import {
    APPLICATION_BY_SKILL_NAME,
    CS_MEASURES,
    GENRE_TYPES,
    MEASURE_BY_SKILL_NAME,
} from '../../../constants/assessments';
import { useAppContext } from '../../../common/contexts/AppContext';
import { BURST, DIBELS8, IDEL2 } from '../../../constants/applications';
import {
    timesFormWasUsedInResults,
    timesFormBurstWasUsedInResults,
} from '../../../common/utils/popupDataHelper';
import MeasureSelectionDropdownLegendItem from '../../../common/popups/MeasureSelectionDropdownLegendItem';
import { getStudentResults, getAllStudentResults } from '../../../common/utils/studentsListHelpers';
import FORM_NAMES_MAP from '../../../constants/forms';
import { isDigitalFormsPopupEnabled } from '../../../common/popups/utils';
import { usePopupContext } from '../../../common/contexts/PopupContext';

const FORM_ID_KEYS = {
    [BURST]: 'probeId',
    [DIBELS8]: 'uid',
    [IDEL2]: 'uid',
};

/**
 * For regular skill we Form name with sequence number but CS measures requires genre short name after.
 * @param application - application name
 * @param form - form object
 * @returns {string} - form name
 */
export const getFormName = (application, form) => {
    switch (application) {
        case DIBELS8: {
            // TODO: Leave only `Form ${form.sequence}` when ORF printed materials would be fixed
            return get(FORM_NAMES_MAP, form.uid, `Form ${form.sequence}`);
        }
        case IDEL2: {
            return `Form ${form.sequence}`;
        }
        case BURST: {
            return `Form ${getBurstSequence(form)}`;
        }
        default:
            return '';
    }
};

const MeasureSelectionPopup = ({
    skillName,
    measureName,
    isPopupOpened,
    onClose,
    formsBySkill,
    grade,
    studentId,
    isScrollLockEnabled,
    initialFormUidsByGrade,
}) => {
    const appName = APPLICATION_BY_SKILL_NAME[skillName];
    const [selectedGrade, setSelectedGrade] = useState(grade);

    const formIdKey = FORM_ID_KEYS[appName];
    const measure = measureName || MEASURE_BY_SKILL_NAME[skillName];
    const [selectedFormUID, setSelectedForm] = useState(get(initialFormUidsByGrade, grade, ''));
    const { checkAndGetSessionStatus, classId, allResults } = useAppContext();
    const {
        userPopupsSettings: { isDigitalPopupEnabled },
        actions: { showPMFormSelectionPopup, showDigitalPopup },
    } = usePopupContext();

    const selectedForm = formsBySkill[selectedGrade].find(
        form => get(form, formIdKey) === selectedFormUID,
    );

    const defaultGenre = get(selectedForm, 'genre', GENRE_TYPES.FICTION);

    const [selectedGenre, setSelectedGenre] = useState(defaultGenre);
    const [currentForm, setCurrentForm] = useState(selectedForm);

    const filteredForms = CS_MEASURES.includes(measure)
        ? formsBySkill[selectedGrade].filter(form => form.genre === selectedGenre)
        : formsBySkill[selectedGrade];

    useEffect(() => {
        if (selectedForm) {
            setCurrentForm(selectedForm);
        }
    }, [selectedForm]);

    useEffect(() => {
        setSelectedForm(get(initialFormUidsByGrade, selectedGrade));
    }, [initialFormUidsByGrade, selectedGrade]);

    const gradeOptions = getSortedGrades(formsBySkill)
        .filter(gradeName => PM_GRADE_AVAILABILITY.includes(gradeName))
        .map(gradeName => ({
            value: gradeName,
            label: <MeasureSelectionDropdownItem label={GRADE_LABEL[gradeName]} />,
        }));

    const changeGenre = genre => {
        setSelectedGenre(genre);
        const forms = formsBySkill[selectedGrade].filter(form => form.genre === genre);
        setSelectedForm(get(forms[0], formIdKey, ''));
    };

    const getSelectedGradeOption = gradeOptions.filter(
        gradeOption => gradeOption.value === selectedGrade,
    )[0];
    const studentResults = getStudentResults(allResults, studentId, getAllStudentResults);

    const sortedForms = getSortedForms(filteredForms).map(formBySkill => {
        const formUid = get(formBySkill, formIdKey);
        return {
            formUid,
            formName: getFormName(appName, formBySkill),
            timesUsedInResults:
                appName === BURST
                    ? timesFormBurstWasUsedInResults(formUid, get(studentResults, appName))
                    : timesFormWasUsedInResults(formUid, get(studentResults, appName)),
            genre: formBySkill.genre,
            sequence: formBySkill.sequence,
        };
    });

    const formOptions = [
        {
            label: <MeasureSelectionDropdownLegendItem />,
            className: classNames('dashboard-option-disabled'),
        },
        ...sortedForms.map(({ formUid, formName, timesUsedInResults, genre }) => ({
            value: formUid,
            label: (
                <MeasureSelectionDropdownItem
                    label={formName}
                    numOfUsedForms={timesUsedInResults}
                    genre={genre}
                />
            ),
        })),
    ];

    const getSelectedFormOption = formOptions.filter(
        formOption => formOption.value === selectedFormUID,
    )[0];

    const sequence = get(currentForm, 'sequence');

    const onSubmitButtonClick = e => {
        const assessmentLink = constructStartPMAssessmentLink(appName, classId, studentId, {
            formUid: selectedFormUID,
            probeConfig: appName === BURST ? formatBurstForm(selectedForm) : null,
            measure,
        });

        if (isDigitalFormsPopupEnabled(sequence, measure, isDigitalPopupEnabled)) {
            showDigitalPopup({
                assessmentLink,
                skillName,
                measureName,
                grade,
                studentId,
                initialFormUidsByGrade,
                onCancelHandler: showPMFormSelectionPopup,
            });
        } else {
            linkOnClickHandler(assessmentLink, checkAndGetSessionStatus)(e);
        }
    };

    return (
        <FormSelectionPopup
            isPopupOpened={isPopupOpened}
            isScrollLockEnabled={isScrollLockEnabled}
            onClose={onClose}
            gradeOptions={gradeOptions}
            getSelectedGradeOption={getSelectedGradeOption}
            setSelectedGrade={setSelectedGrade}
            formOptions={formOptions}
            getSelectedFormOption={getSelectedFormOption}
            setSelectedForm={setSelectedForm}
            selectedGenre={selectedGenre}
            setSelectedGenre={changeGenre}
            skillName={skillName}
            measureName={measureName}
            onClick={onSubmitButtonClick}
        />
    );
};

MeasureSelectionPopup.propTypes = {
    skillName: PropTypes.string.isRequired,
    measureName: PropTypes.string,
    isPopupOpened: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    formsBySkill: PropTypes.object.isRequired,
    grade: PropTypes.string.isRequired,
    studentId: PropTypes.string.isRequired,
    isScrollLockEnabled: PropTypes.bool.isRequired,
    initialFormUidsByGrade: PropTypes.object.isRequired,
};

MeasureSelectionPopup.defaultProps = {
    measureName: undefined,
};

export default MeasureSelectionPopup;
